import { formatDate } from "@progress/kendo-intl";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { LoadingIndicator } from "../../../core/loading-indicator.component";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { useGetSessions } from "../../../apiclient/master/useGetSessionApi";
import { SelectField } from "../../../core/select-field.component";
import { Form, Formik } from "formik";
import { useGetProducts } from "../../../apiclient/product/useGetProductApi";
import { CheckboxField, CheckBoxStyle } from "../../../core/checkbox-field.component";
import { PDFExport } from "@progress/kendo-react-pdf";
import { useGetCompanyDetails } from "../../../apiclient/master/useGetCompanydetailsApi";

interface IFermentedMilkJSONReport { }

export const FermentedMilkJSONReport: React.FC = () => {
    const [reportMenu, setReportMenu] = useState<IFermentedMilkJSONReport[]>([]);
    const [productData, setProductData] = useState<{ value: string; label: string }[]>([]);
    const { data: fetchedProduct } = useGetProducts();
    const [fromDate, setFromDate] = useState<Date>(
        new Date(dayjs().add(0, "months").format("MM/DD/YYYY"))
    );
    const [toDate, setToDate] = useState<Date>(new Date());

    const { data: companyDetails, isLoading: isCompanyLoading } =
    useGetCompanyDetails();
  const companyName = companyDetails?.[0]?.companyName || "Company Name";

    // State for controlling additional checkboxes visibility
    const [showInstitutionCheckboxes, setShowInstitutionCheckboxes] = useState(false);

    // Update the visibility of institution checkboxes based on the "By Institution Type" checkbox
    const handleByInstitutionTypeChange = (checked: boolean) => {
        setShowInstitutionCheckboxes(checked);
    };

    useEffect(() => {
        if (fetchedProduct) {
            const mappedProduct = fetchedProduct.map((product: any) => ({
                value: product.productId,
                label: product.name,
            }));
            setProductData(mappedProduct);
        }
    }, [fetchedProduct]);
    const exportExcel = useRef<ExcelExport | null>(null);
    const pdfExportRef = useRef<PDFExport | null>(null);

    
    const excelExport = () => {
        if (!exportExcel.current) return;
    
        const options = exportExcel.current.workbookOptions();
        const sheet = options.sheets[0];
    
        const formattedFromDate = fromDate
          ? new Date(fromDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "numeric",
              year: "numeric",
            })
          : "";
    
        const formattedToDate = toDate
          ? new Date(toDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "numeric",
              year: "numeric",
            })
          : "";
    
        const headingWithDate = `MonthWise_Comparative - ${formattedFromDate} to ${formattedToDate}`;
    
        // Add a single merged heading row with the full string
        sheet.rows.unshift({
          cells: [
            {
              value: headingWithDate,
              colSpan: sheet.columns.length,
              bold: true,
              fontSize: 14,
              textAlign: "center",
            },
          ],
        });
    
        sheet.rows.unshift({
          cells: [
            {
              value: companyName,
              colSpan: sheet.columns.length,
              bold: true,
              fontSize: 14,
              textAlign: "center",
            },
          ],
        });
    
        exportExcel.current.save(options);
      };

    const exportToPDF = () => {
        if (pdfExportRef.current) {
            pdfExportRef.current.save();
        }
    };
    // const hasRecords = girdData.length > 0; 

    // Initial values for Formik
    const initialValues = {
        sessionId: "", // default empty value for zone
        fromDate: fromDate,
        toDate: toDate,
        byInstitutionType: false, // default value for the checkbox
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
                console.log("Form submitted with values: ", values);
            }}
        >
            {({ setFieldValue, values }) => {
                return (
                    <Form>
                        <div
                            className="m-lg-3 mt-lg-1 m-4 card p-3 border-0 m-3 sw-shadow radius-15"
                            style={{ minHeight: "500px" }}
                        >
                            <div className="top-sec-align row">
                                <center>
                                    <h4 className="text-normal title-web mb-0 report-title-color">
                                        Fermented Milk JSON Report
                                    </h4>
                                </center>
                            </div>
                            <div className="fade_rule"></div>
                            <div className="d-flex justify-content-between mt-4 pt-3 pb-3">
                                <div className="d-flex">
                                    <div className="me-2">
                                        <label>From Date</label>
                                        <DatePicker
                                            selectsMultiple={true}
                                            openToDate={new Date()}
                                            className="form-control ms-auto search-right-align calendarBg"
                                            value={formatDate(fromDate, "dd/MM/yyyy")}
                                            placeholderText="From"
                                            dateFormat={"dd/MM/yyyy"}
                                            onChange={([date]: any) => {
                                                setFromDate(date);
                                            }}
                                        />
                                    </div>
                                    <div className="me-2">
                                        <label>To Date</label>
                                        <DatePicker
                                            selectsMultiple={true}
                                            openToDate={new Date()}
                                            className="form-control ms-auto search-right-align calendarBg"
                                            value={formatDate(toDate, "dd/MM/yyyy")}
                                            placeholderText="To"
                                            dateFormat={"dd/MM/yyyy"}
                                            onChange={([date]: any) => {
                                                setToDate(date);
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label></label>
                                        <button
                                            className="btn-primary btn search-btns mt-4"
                                            type="submit"
                                        >
                                            <svg width="17" height="17" viewBox="0 0 17.5 17.5">
                                                <path
                                                    id="Path_magnify"
                                                    data-name="Path / magnify"
                                                    d="M9.5,3a6.5,6.5,0,0,1,4.94,10.73l.27.27h.79l5,5L19,20.5l-5-5v-.79l-.27-.27A6.5,6.5,0,1,1,9.5,3m0,2A4.5,4.5,0,1,0,14,9.5,4.481,4.481,0,0,0,9.5,5Z"
                                                    transform="translate(-3 -3)"
                                                    fill="#fff"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="d-flex justify-content-end">
                                        <button
                                            title="Export Excel"
                                            className="export-button ms-2"
                                            onClick={excelExport}
                                            // disabled={!hasRecords}
                                            style={{ marginTop: "23px" }}
                                        >
                                            Export to Excel
                                        </button>
                                        <button
                                            title="Export Excel"
                                            className="export-button ms-2"
                                            onClick={exportToPDF}
                                            // disabled={!hasRecords}
                                            style={{ marginTop: "23px" }}
                                        >
                                            Export to PDF
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex">
                                <div className="me-5">
                                    <CheckboxField
                                        label="By Institution Type"
                                        name="byInstitutionType"
                                        checkstyle={CheckBoxStyle.Normal}
                                    // Use Formik to handle checkbox value
                                    // onChange={(e: any) => {
                                    //     setFieldValue("byInstitutionType", e.target.checked);
                                    //     handleByInstitutionTypeChange(e.target.checked); // Show/hide additional checkboxes based on state
                                    // }}
                                    />
                                </div>

                                <div className="me-5">
                                    <CheckboxField
                                        label="By Booth Number"
                                        name="byBoothNumber"
                                        checkstyle={CheckBoxStyle.Normal}
                                    />
                                </div>
                            </div>

                            {/* Show additional checkboxes when 'By Institution Type' is checked */}
                            {showInstitutionCheckboxes && (
                                <div className="d-flex flex-column ms-5">
                                    <CheckboxField label="Institution Type 1" name="institutionType1" checkstyle={CheckBoxStyle.Normal} />
                                    <CheckboxField label="Institution Type 2" name="institutionType2" checkstyle={CheckBoxStyle.Normal} />
                                    <CheckboxField label="Institution Type 3" name="institutionType3" checkstyle={CheckBoxStyle.Normal} />
                                    <CheckboxField label="Institution Type 4" name="institutionType4" checkstyle={CheckBoxStyle.Normal} />
                                    <CheckboxField label="Institution Type 5" name="institutionType5" checkstyle={CheckBoxStyle.Normal} />
                                </div>
                            )}
                        <div className="row col=12">
                            <div>
                                <LoadingIndicator isLoading={false} />
                                <PDFExport
                  fileName={"Fermented_Milk_JSON.pdf"}
                  ref={pdfExportRef}
                  title="Fermented Milk JSON"
                >
                  <div style={{ padding: "10px", backgroundColor: "#fff" }}>
                    {/* Company Name */}
                    <div style={{ textAlign: "center", marginBottom: "5px" }}>
                      <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                        {companyDetails?.[0]?.companyName || "Company Name"}
                      </span>
                    </div>
                    <div style={{ textAlign: "center", marginBottom: "10px" }}>
                      <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                            Fermented_Milk_JSON
                        {fromDate && toDate && (
                          <>
                            {" - "}
                            {new Date(fromDate).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "numeric",
                              year: "numeric",
                            })}{" "}
                            to{" "}
                            {new Date(toDate).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "numeric",
                              year: "numeric",
                            })}
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </PDFExport>
                            </div>
                        </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};















